import { useState, useCallback, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { adminApi, type ChapterSection } from "../../api/adminApi";

export interface WYSIWYGEditorState {
  // Datos
  sections: ChapterSection[];

  // Navegación
  currentSectionIndex: number;
  currentSectionId: string | null;

  // Edición
  editingState: {
    isEditing: boolean;
    sectionId: string | null;
    field: "content" | "title" | null;
    originalValue: string;
    currentValue: string;
    isDirty: boolean;
  };

  // UI
  isPanelCollapsed: boolean;
  activeToolbar: "audio" | "diagram" | "ai" | null;

  // Estado de guardado
  isSaving: boolean;
  lastSavedAt: Date | null;
}

export interface WYSIWYGEditorActions {
  // Navegación
  goToSection: (index: number) => void;
  goToNext: () => void;
  goToPrev: () => void;

  // Edición
  startEditing: (sectionId: string, field: "content" | "title") => void;
  updateContent: (value: string) => void;
  saveAndStopEditing: () => Promise<void>;
  cancelEditing: () => void;

  // Secciones
  addSection: (
    afterIndex?: number,
    options?: { withAI?: boolean; topic?: string },
  ) => Promise<ChapterSection | undefined>;
  deleteSection: (sectionId: string) => Promise<void>;
  reorderSections: (fromIndex: number, toIndex: number) => Promise<void>;

  // UI
  togglePanel: () => void;
  setActiveToolbar: (toolbar: "audio" | "diagram" | "ai" | null) => void;

  // Refetch
  refetchSections: () => void;
}

const AUTOSAVE_DELAY_MS = 1000;
const DRAFT_STORAGE_KEY = "wysiwyg-draft";

interface DraftData {
  sectionId: string;
  field: "content" | "title";
  value: string;
  timestamp: number;
}

function saveDraftToStorage(draft: DraftData | null): void {
  if (draft) {
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
  } else {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
  }
}

function getDraftFromStorage(): DraftData | null {
  try {
    const data = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (data) {
      return JSON.parse(data);
    }
  } catch {
    // Ignore parse errors
  }
  return null;
}

export function useWYSIWYGEditor(chapterId: string): {
  state: WYSIWYGEditorState;
  actions: WYSIWYGEditorActions;
  isLoading: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [editingState, setEditingState] = useState<
    WYSIWYGEditorState["editingState"]
  >({
    isEditing: false,
    sectionId: null,
    field: null,
    originalValue: "",
    currentValue: "",
    isDirty: false,
  });
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
  const [activeToolbar, setActiveToolbar] = useState<
    "audio" | "diagram" | "ai" | null
  >(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  // Ref para evitar llamadas duplicadas
  const savingRef = useRef(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Helper to validate ID (UUID or CUID)
  const isValidId = (id: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      id,
    ) || /^c[a-z0-9]{20,}$/i.test(id);

  // Query para obtener las secciones - only if chapterId is valid
  const {
    data: sections = [],
    isLoading: isLoadingSections,
    error: sectionsError,
    refetch: refetchSections,
  } = useQuery({
    queryKey: ["admin-chapter-sections", chapterId],
    queryFn: () => {
      if (!chapterId || !isValidId(chapterId)) {
        console.warn("[useWYSIWYGEditor] Invalid chapterId:", chapterId);
        return [];
      }
      return adminApi.getChapterSections(chapterId);
    },
    enabled: !!chapterId && isValidId(chapterId),
  });

  // Mutation para actualizar sección
  const updateSectionMutation = useMutation({
    mutationFn: async ({
      sectionId,
      data,
    }: {
      sectionId: string;
      data: { title?: string; content?: string };
    }) => {
      return adminApi.updateSection(sectionId, data);
    },
    onMutate: async ({ sectionId, data }) => {
      // Cancelar queries pendientes
      await queryClient.cancelQueries({
        queryKey: ["admin-chapter-sections", chapterId],
      });

      // Guardar estado anterior
      const previousSections = queryClient.getQueryData<ChapterSection[]>([
        "admin-chapter-sections",
        chapterId,
      ]);

      // Optimistic update
      if (previousSections) {
        queryClient.setQueryData<ChapterSection[]>(
          ["admin-chapter-sections", chapterId],
          previousSections.map((s) =>
            s.id === sectionId ? { ...s, ...data } : s,
          ),
        );
      }

      return { previousSections };
    },
    onError: (_err, _variables, context) => {
      // Rollback en caso de error
      if (context?.previousSections) {
        queryClient.setQueryData(
          ["admin-chapter-sections", chapterId],
          context.previousSections,
        );
      }
    },
    onSuccess: () => {
      setLastSavedAt(new Date());
      saveDraftToStorage(null);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-chapter-sections", chapterId],
      });
    },
  });

  // Mutation para crear sección
  const createSectionMutation = useMutation({
    mutationFn: async (data: {
      title: string;
      content: string;
      position?: number;
    }) => {
      return adminApi.createSection(chapterId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-chapter-sections", chapterId],
      });
    },
  });

  // Mutation para eliminar sección
  const deleteSectionMutation = useMutation({
    mutationFn: async (sectionId: string) => {
      return adminApi.deleteSection(sectionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-chapter-sections", chapterId],
      });
    },
  });

  // Mutation para reordenar secciones
  const reorderSectionsMutation = useMutation({
    mutationFn: async (sectionIds: string[]) => {
      return adminApi.reorderSections(chapterId, sectionIds);
    },
    onMutate: async (newOrder) => {
      await queryClient.cancelQueries({
        queryKey: ["admin-chapter-sections", chapterId],
      });

      const previousSections = queryClient.getQueryData<ChapterSection[]>([
        "admin-chapter-sections",
        chapterId,
      ]);

      // Optimistic update: reorder sections
      if (previousSections) {
        const reordered = newOrder
          .map((id) => previousSections.find((s) => s.id === id))
          .filter(Boolean) as ChapterSection[];
        queryClient.setQueryData(
          ["admin-chapter-sections", chapterId],
          reordered,
        );
      }

      return { previousSections };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousSections) {
        queryClient.setQueryData(
          ["admin-chapter-sections", chapterId],
          context.previousSections,
        );
      }
    },
  });

  // Debounced save using setTimeout
  const debouncedSave = useCallback(
    (sectionId: string, field: "content" | "title", value: string) => {
      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new timer
      debounceTimerRef.current = setTimeout(async () => {
        if (savingRef.current) return;
        savingRef.current = true;
        setIsSaving(true);

        try {
          await updateSectionMutation.mutateAsync({
            sectionId,
            data: field === "content" ? { content: value } : { title: value },
          });
        } finally {
          savingRef.current = false;
          setIsSaving(false);
        }
      }, AUTOSAVE_DELAY_MS);
    },
    [updateSectionMutation],
  );

  // Flush debounced save immediately
  const flushDebouncedSave = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  // Cancel debounced save
  const cancelDebouncedSave = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  // Navegar a sección
  const goToSection = useCallback(
    (index: number) => {
      if (index >= 0 && index < sections.length) {
        // Si hay edición activa, cancelar debounce (ya se guardó o se perderá)
        flushDebouncedSave();
        setCurrentSectionIndex(index);
        setEditingState({
          isEditing: false,
          sectionId: null,
          field: null,
          originalValue: "",
          currentValue: "",
          isDirty: false,
        });
      }
    },
    [sections.length, flushDebouncedSave],
  );

  const goToNext = useCallback(() => {
    goToSection(currentSectionIndex + 1);
  }, [currentSectionIndex, goToSection]);

  const goToPrev = useCallback(() => {
    goToSection(currentSectionIndex - 1);
  }, [currentSectionIndex, goToSection]);

  // Iniciar edición
  const startEditing = useCallback(
    (sectionId: string, field: "content" | "title") => {
      const section = sections.find((s) => s.id === sectionId);
      if (!section) return;

      const value = field === "content" ? section.content : section.title;

      setEditingState({
        isEditing: true,
        sectionId,
        field,
        originalValue: value,
        currentValue: value,
        isDirty: false,
      });
    },
    [sections],
  );

  // Actualizar contenido
  const updateContent = useCallback(
    (value: string) => {
      setEditingState((prev) => ({
        ...prev,
        currentValue: value,
        isDirty: value !== prev.originalValue,
      }));

      // Guardar draft localmente
      if (editingState.sectionId && editingState.field) {
        saveDraftToStorage({
          sectionId: editingState.sectionId,
          field: editingState.field,
          value,
          timestamp: Date.now(),
        });

        // Trigger auto-save
        debouncedSave(editingState.sectionId, editingState.field, value);
      }
    },
    [editingState.sectionId, editingState.field, debouncedSave],
  );

  // Guardar y salir de edición
  const saveAndStopEditing = useCallback(async () => {
    // Cancel pending debounce
    cancelDebouncedSave();

    if (editingState.isDirty && editingState.sectionId && editingState.field) {
      await updateSectionMutation.mutateAsync({
        sectionId: editingState.sectionId,
        data:
          editingState.field === "content"
            ? { content: editingState.currentValue }
            : { title: editingState.currentValue },
      });
    }

    setEditingState({
      isEditing: false,
      sectionId: null,
      field: null,
      originalValue: "",
      currentValue: "",
      isDirty: false,
    });
    saveDraftToStorage(null);
  }, [editingState, cancelDebouncedSave, updateSectionMutation]);

  // Cancelar edición
  const cancelEditing = useCallback(() => {
    cancelDebouncedSave();
    setEditingState({
      isEditing: false,
      sectionId: null,
      field: null,
      originalValue: "",
      currentValue: "",
      isDirty: false,
    });
    saveDraftToStorage(null);
  }, [cancelDebouncedSave]);

  // Añadir sección
  const addSection = useCallback(
    async (
      afterIndex?: number,
      options?: { withAI?: boolean; topic?: string },
    ) => {
      const position =
        afterIndex !== undefined ? afterIndex + 2 : sections.length + 1;

      let title = `Nueva sección ${position}`;
      let content = "<p>Contenido de la sección...</p>";

      if (options?.withAI) {
        const generated = await adminApi.generateSectionWithAI(chapterId, {
          topic: options.topic,
        });
        title = generated.title;
        content = generated.content;
      }

      const newSection = await createSectionMutation.mutateAsync({
        title,
        content,
        position,
      });

      // Navegar a la nueva sección
      const newIndex =
        afterIndex !== undefined ? afterIndex + 1 : sections.length;
      setCurrentSectionIndex(newIndex);

      return newSection;
    },
    [sections.length, createSectionMutation, chapterId],
  );

  // Eliminar sección
  const deleteSection = useCallback(
    async (sectionId: string) => {
      await deleteSectionMutation.mutateAsync(sectionId);

      // Ajustar índice si es necesario
      const deletedIndex = sections.findIndex((s) => s.id === sectionId);
      if (deletedIndex <= currentSectionIndex && currentSectionIndex > 0) {
        setCurrentSectionIndex(currentSectionIndex - 1);
      }
    },
    [deleteSectionMutation, sections, currentSectionIndex],
  );

  // Reordenar secciones
  const reorderSections = useCallback(
    async (fromIndex: number, toIndex: number) => {
      const newOrder = [...sections];
      const [moved] = newOrder.splice(fromIndex, 1);
      if (moved) {
        newOrder.splice(toIndex, 0, moved);
      }

      await reorderSectionsMutation.mutateAsync(newOrder.map((s) => s.id));

      // Actualizar índice actual si se movió la sección seleccionada
      if (fromIndex === currentSectionIndex) {
        setCurrentSectionIndex(toIndex);
      } else if (
        fromIndex < currentSectionIndex &&
        toIndex >= currentSectionIndex
      ) {
        setCurrentSectionIndex(currentSectionIndex - 1);
      } else if (
        fromIndex > currentSectionIndex &&
        toIndex <= currentSectionIndex
      ) {
        setCurrentSectionIndex(currentSectionIndex + 1);
      }
    },
    [sections, reorderSectionsMutation, currentSectionIndex],
  );

  // Toggle panel
  const togglePanel = useCallback(() => {
    setIsPanelCollapsed((prev) => !prev);
  }, []);

  // Warning antes de salir si hay cambios sin guardar
  useEffect(() => {
    if (editingState.isDirty) {
      const handler = (e: BeforeUnloadEvent) => {
        e.preventDefault();
        e.returnValue = "";
      };
      window.addEventListener("beforeunload", handler);
      return () => window.removeEventListener("beforeunload", handler);
    }
  }, [editingState.isDirty]);

  // Current section
  const currentSection = sections[currentSectionIndex];

  const state: WYSIWYGEditorState = {
    sections,
    currentSectionIndex,
    currentSectionId: currentSection?.id || null,
    editingState,
    isPanelCollapsed,
    activeToolbar,
    isSaving,
    lastSavedAt,
  };

  const actions: WYSIWYGEditorActions = {
    goToSection,
    goToNext,
    goToPrev,
    startEditing,
    updateContent,
    saveAndStopEditing,
    cancelEditing,
    addSection,
    deleteSection,
    reorderSections,
    togglePanel,
    setActiveToolbar,
    refetchSections: () => refetchSections(),
  };

  return {
    state,
    actions,
    isLoading: isLoadingSections,
    error: sectionsError || null,
  };
}

export type { ChapterSection };
